import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { camera, scene } from './scene';
import { onQuality } from '../platform/quality';

/* ============================================================
   後製 —— 門檻式泛光（bloom）
   ============================================================

   使用者 2026-08-04：「把場景質感提升到產品水準」，並在選項裡挑了
   **半解析度門檻 bloom**。它要解決的是這個畫面最根本的缺陷：
   推進器、彈道、爆炸、窗燈在螢幕上只是「亮色的像素」，不是「光」——
   真實的光會溢到周圍，而那正是「有做光影」與「有調顏色」的差別。

   ## 三個 r128 專屬的坑（照抄新版範例會壞）

   1. **composer 的 render target 一定要設 `sRGBEncoding`。**
      r128 的 `WebGLRenderer` 是看「當前 render target 的 encoding」決定要不要
      做 linear→sRGB；EffectComposer 建出來的 target 預設是 LinearEncoding，
      於是場景被以 linear 值寫進去、最後原封不動貼上螢幕 ——
      畫面會整片泛白發灰（而且看起來像「bloom 太強」，離真因很遠）。

   2. **`UnrealBloomPass` 會把 ACES 套第二次。** 它當最後一關時，底圖是用
      內建的 `MeshBasicMaterial` 貼上螢幕的，而那顆材質的 `toneMapped` 預設
      為 true —— 場景在 RenderPass 已經 tone map 過一次，這裡再來一次。
      實測中間調掉 30–43%（同種子同角度 p50 31.2 → 17.7），而症狀是
      「**開了 bloom 畫面反而變暗**」—— bloom 是加算的、理論上不可能變暗，
      所以這個症狀會把人往完全錯的方向帶（我第一輪就去查編碼設定了）。
      修法是 `bloomPass.basic.toneMapped = false`。

   3. **門檻值不能照抄範例的 0.85 那一族。** 見 `BLOOM` 上面那段。

   4. **`setSize` 要跟著動態解析度走。** `platform/resolution.ts` 會在幀時間
      吃緊時改 `setPixelRatio` —— composer 不跟著改的話，畫面會被拉伸取樣
      （症狀是「降解析度之後畫面糊掉而且位移」）。

   ## 效能

   bloom 是全螢幕 pass，跟動態解析度搶的是同一份 fragment 預算。
   所以模糊跑在**半解析度**（`RES_SCALE`）：UnrealBloomPass 自己會再往下
   降階，起點減半等於整條 mip 鏈都便宜四倍，而泛光本來就是低頻訊號，
   半解析度在畫面上看不出差別。 */

var composer: any = null;
var bloomPass: any = null;
var renderPass: any = null;
var enabled = false;
var rendererRef: any = null;

/** 模糊鏈的起始解析度倍率。0.5 ＝ 半解析度（見檔頭「效能」）。 */
var RES_SCALE = 0.5;

/** 泛光的三個旋鈕。**門檻是最敏感的那個。**
    2026-08-04 掃參數定的（`tools/bloom-sweep.mjs`，八組同種子同角度）：
      門檻 0.50 以下 → 混凝土的受光面開始發霧，畫面失去對比
      門檻 0.78 以上 → 只有火心會亮，等於沒做
    落在 0.66 是「火、窗燈、彈道、爆炸會發光，被照亮的東西不會」的分界。
    ⚠️ 這個門檻是**畫面實際亮度**的門檻，不是 HDR 亮度 —— 整張圖有 99% 的
    像素在 0.24 以下（實測），所以任何抄自 HDR 範例的門檻值都不成立。 */
/* ⚠️ 2026-08-05 使用者三次回報「泛光太重、像魔法攻擊」之後下修：
   強度 1.15 → 0.62、門檻 0.66 → 0.74。上面那組是 2026-08-04 為**場景質感**
   掃出來的（火、窗燈要發光），但武器特效進場之後同一組門檻讓每一發子彈
   都在畫面上暈開一次 —— 場景一秒鐘只有幾個火點，武器一秒鐘有十幾發。
   門檻 0.74 之後只剩「真的在燒」的東西會過（火心、槍口那 0.06 秒）。
   要調回去看得見的場景光，改 strength 不要改 threshold。 */
/* ⚠️ 2026-08-05 第四版（硬邊幾何／白光）落地後再調一次：
   強度 0.62 → **1.15**（還原成 08-04 為場景質感掃出來的值），
   門檻 0.74 → **0.84**（往上，不是往下）。

   硬邊風格不靠泛光 —— 它的量體來自「不透明的形狀會遮住背後」，
   所以場景的火光與窗燈可以拿回原本的強度，而武器的白色平塗只要
   留在門檻下就不會暈開。第一次實拍就驗到：門檻 0.66 時不透明度 1 的
   白色多邊形整片被糊成一顆球，硬邊風格最怕的就是這個。

   上一段那條「要調回去看得見的場景光，改 strength 不要改 threshold」
   講的是**調低**門檻的方向（會讓武器特效整批衝回來），與這裡不衝突。 */
export var BLOOM = { strength: 1.15, radius: 0.6, threshold: 0.84 };

/** 建立後製鏈。`renderer` 為 null（headless）時整組不建立，
    呼叫端靠 `postActive()` 決定要不要走 `composer.render()`。 */
export function initPostFX(renderer: any) {
  if (!renderer) return;
  rendererRef = renderer;
  var size = renderer.getSize(new THREE.Vector2());
  composer = new EffectComposer(renderer);
  /* 見檔頭第 1 點。兩張都要設 —— composer 每個 pass 交替讀寫這兩張，
     只設一張的話畫面會**每幀在正常與泛白之間閃**。 */
  var srgb = (THREE as any).sRGBEncoding;
  if (srgb) {
    composer.renderTarget1.texture.encoding = srgb;
    composer.renderTarget2.texture.encoding = srgb;
  }
  renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);
  bloomPass = new UnrealBloomPass(
    new THREE.Vector2(size.x * RES_SCALE, size.y * RES_SCALE),
    BLOOM.strength, BLOOM.radius, BLOOM.threshold);
  /* ⚠️ **最重要的一行**（見檔頭第 4 點）。`UnrealBloomPass` 是最後一關時，
     底圖是用它內建的 `basic`（一顆 `MeshBasicMaterial`）貼上螢幕的，
     而 `MeshBasicMaterial.toneMapped` 預設是 true —— 於是 ACES 被**套第二次**。
     實測：同一顆種子、同一個角度，中間調掉 30–43%（p50 31.2 → 17.7）。
     症狀是「開了 bloom 反而整個畫面變暗」，而 bloom 是加算的、理論上不可能變暗。 */
  bloomPass.basic.toneMapped = false;
  composer.addPass(bloomPass);
  /* ⚠️ **draw call 的統計要改成手動 reset**。`renderer.info` 預設每呼叫一次
     `render()` 就歸零，而 composer 一幀會呼叫十幾次 —— 於是 `mapStats()`
     報出來的是「最後一個 pass」（calls: 1），整個 draw call 預算的護欄
     靜靜失效。改成手動之後拿到的是**整幀的累計**，比原本更準。
     呼叫端（`game.ts`）負責每幀開頭 `renderer.info.reset()`。 */
  renderer.info.autoReset = false;
  enabled = true;
  resizePostFX();
  /* 泛光歸畫質設定管。`onQuality` 註冊當下就會呼叫一次，
     所以這一行同時是「套用開機時讀回來的設定」。 */
  onQuality(function (q) { setPostFX(q.bloom); });
}

/** 開關泛光。關掉時 `postActive()` 回 false，呼叫端走原本的 `renderer.render`
    —— 這樣「關掉後製」是真的零成本，不是「跑一個什麼都不做的 pass」。 */
export function setPostFX(on: boolean) {
  enabled = !!on && !!composer;
}
export function postActive() { return enabled && !!composer; }

/** 套用泛光參數（給除錯主控台與畫質設定用）。 */
export function tunePostFX(o: any) {
  if (!bloomPass) return;
  if (o.strength !== undefined) bloomPass.strength = BLOOM.strength = o.strength;
  if (o.radius !== undefined) bloomPass.radius = BLOOM.radius = o.radius;
  if (o.threshold !== undefined) bloomPass.threshold = BLOOM.threshold = o.threshold;
}

/** 視窗尺寸或像素比改變時要呼叫（見檔頭第 3 點）。 */
export function resizePostFX() {
  if (!composer || !rendererRef) return;
  var s = rendererRef.getSize(new THREE.Vector2());
  composer.setPixelRatio(rendererRef.getPixelRatio());
  composer.setSize(s.x, s.y);
  if (bloomPass) bloomPass.setSize(s.x * RES_SCALE, s.y * RES_SCALE);
}

export function renderPostFX() {
  if (composer) composer.render();
}
